import { css } from 'aphrodite';
import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { ITemplate, RejectionCategory, ReviewStatus } from '../../../../../extViewmodels';
import { ILocationState } from '../../../../../models';
import { useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { checkSocialCitation, getSocialMediaPlainTextContentFromRawRichContent } from '../../../../../models/UiUtils';
import { invalidateSocialAllCategoryTemplates, invalidateSocialCategoryTemplates } from '../../../../../queries';
import { useCreateTemplateReviewMutation } from '../../../../../queries/Templates/useCreateTemplateReviewMutation';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../components/MultiContainerHeader';
import {
	BasicVideoPreview,
	InstagramImagePreview,
	PreviewPostContentView,
} from '../../../../components/socialMedia/PreviewSocialMediaPost/presentation';
import { getTemplateApprovalStatus } from '../../../../components/TemplateReviewStatusIndicator';
import { TemplateRejectionModal } from '../../../../components/templates/TemplateRejectionModal';
import { baseStyleSheet } from '../../../../styles/styles';
import { getEnabledReviewApprovalStatus } from '../utils';
import { styles } from './styles';
import { TemplateRejectionReason } from './types';

export const ReviewSocialPost: React.FC = () => {
	const history = useHistory();
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const loc: ILocationState<ITemplate, any> = history.location.state;
	const template = loc?.model?.context as ITemplate;

	const [showCitations, setShowCitations] = useState(false);
	const [imagePreviewModalOpen, setImagePreviewModalOpen] = useState(false);
	const [showRejectionModal, setShowRejectionModal] = useState(false);
	const [initialImageUrl, setInitialImageUrl] = useState<string>('');

	const { mutate: createTemplateReview, isPending: isReviewLoading } = useCreateTemplateReviewMutation();

	const invalidateTemplates = () => {
		invalidateSocialAllCategoryTemplates();
		invalidateSocialCategoryTemplates();
	};

	const handleApprove = () => {
		const enabledStatus = getEnabledReviewApprovalStatus(userSession.account);
		if (!enabledStatus) {
			toast.error('No template approval status is enabled on this account.');
			return;
		}

		createTemplateReview(
			{
				templateId: template.id,
				reviewRequest: { id: template.id, status: enabledStatus, reason: null },
			},
			{
				onSuccess: () => {
					invalidateTemplates();
					toast.success('Template approved successfully.');
					fullscreenModal.dismissModal();
				},
				onError: () => {
					invalidateTemplates();
					toast.error('Failed to approve the template.');
				},
			}
		);
	};

	const handleReject = (reasoning: string, rejectionCategory: RejectionCategory) =>
		createTemplateReview(
			{
				templateId: template.id,
				reviewRequest: {
					id: template.id,
					status: ReviewStatus.Rejected,
					reason: { reasoning, rejectionCategory } as TemplateRejectionReason,
				},
			},
			{
				onSuccess: () => {
					invalidateTemplates();
					toast.success('Template rejected successfully.');
					fullscreenModal.dismissModal();
				},
				onError: () => {
					invalidateTemplates();
					toast.error('Failed to reject the template.');
				},
			}
		);

	const handleEnlargeImage = (imageUrl: string) => {
		setInitialImageUrl(imageUrl);
		setImagePreviewModalOpen(true);
	};

	const handleCloseImagePreview = () => {
		setImagePreviewModalOpen(false);
	};

	const handleViewCitations = () => {
		setShowCitations(true);
	};

	const handleCloseCitations = () => {
		setShowCitations(false);
	};

	const approvalStatus = getTemplateApprovalStatus(template?.templateReview);
	const hasAttachments = template?.attachments?.length > 0;
	const hasVideoAttachments = hasAttachments && template.attachments.some(x => x.mimeType?.startsWith('video/'));
	const isRejected = template?.templateReview?.status === ReviewStatus.Rejected;
	const isApproved =
		template?.templateReview?.status === ReviewStatus.PreApproved ||
		template?.templateReview?.status === ReviewStatus.PreReviewed;

	return (
		<div className={css(styles.container)}>
			<MultiContainerHeader fullscreenHeader='Review Social Media Post' />
			<div className={css(styles.flex)}>
				<div className={css(styles.leftContainer)}>
					<div className={css(styles.previewContainerHeader)}>
						<div>{template?.name}</div>
						<div className={css(baseStyleSheet.horizontalStack, baseStyleSheet.gap4)}>
							{checkSocialCitation(template) && (
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleViewCitations}>
									<span>View Citations</span>
								</button>
							)}
							{approvalStatus}
						</div>
					</div>
					<div className={css(styles.previewContainer)}>
						<section className={`preview-social-media-post ${css(styles.previewContentContainer)}`}>
							<figure className={css(styles.previewContentBox)}>
								<PreviewPostContentView
									previewContent={getSocialMediaPlainTextContentFromRawRichContent(template?.content)}
									postContent={getSocialMediaPlainTextContentFromRawRichContent(template?.content)}
								/>
								{hasAttachments &&
									(hasVideoAttachments ? (
										<BasicVideoPreview postImages={template.attachments} controls={true} />
									) : (
										<InstagramImagePreview postImages={template.attachments} onClickEnlargeImage={handleEnlargeImage} />
									))}
							</figure>
						</section>
					</div>
				</div>

				<div className={css(styles.rightContainer)}>
					<div>
						<div className={css(styles.header)}>
							<h3 className={css(styles.title)}>Campaign Approval</h3>
							<p className={css(styles.subtitle)}>Preview campaign for compliance approval</p>
							<hr className={css(styles.divider)} />
						</div>

						{isRejected && template?.templateReview?.reason && (
							<div className={css(styles.rejectionInfo)}>
								<div className={css(styles.rejectionReasonSection)}>
									<p className={css(styles.rejectionReasonTitle)}>Reason for rejection:</p>
									<ul className={css(styles.rejectionCategoryList)}>
										<li className={css(styles.rejectionCategoryItem)}>
											{'- ' + template.templateReview.reason.rejectionCategory}
										</li>
									</ul>
								</div>

								<div className={css(styles.rejectionNotesSection)}>
									<p className={css(styles.rejectionNotesTitle)}>Notes:</p>
									<p className={css(styles.rejectionNotesContent)}>{template.templateReview.reason.reasoning}</p>
								</div>
							</div>
						)}
					</div>
					<div className={css(styles.footer)}>
						{!isApproved && (
							<button className={css(baseStyleSheet.ctaButton)} onClick={handleApprove} disabled={isReviewLoading}>
								{isReviewLoading ? <LoadingSpinner type='small' /> : <span>Approve Campaign</span>}
							</button>
						)}
						{!isRejected && (
							<button
								className={css(baseStyleSheet.ctaButtonReverse, styles.rejectButton)}
								onClick={() => setShowRejectionModal(true)}
								disabled={isReviewLoading}
							>
								{isReviewLoading ? <LoadingSpinner type='small' /> : <span>Reject Campaign</span>}
							</button>
						)}
					</div>
				</div>
			</div>

			<TemplateRejectionModal
				isOpen={showRejectionModal}
				onRequestClose={() => setShowRejectionModal(false)}
				onSubmit={handleReject}
			/>

			<ReactModal
				onRequestClose={handleCloseImagePreview}
				isOpen={imagePreviewModalOpen}
				className={css(styles.modalContent)}
				ariaHideApp={!imagePreviewModalOpen}
				overlayClassName={css(styles.modalOverlay)}
			>
				<button onClick={handleCloseImagePreview} className={css(styles.closeButton)}>
					Close
				</button>
				<img src={initialImageUrl} alt='Preview' className={css(styles.previewImage)} />
			</ReactModal>

			<ReactModal
				isOpen={showCitations}
				onRequestClose={handleCloseCitations}
				className={css(styles.citationModal)}
				overlayClassName={css(styles.modalOverlay)}
			>
				<div className={css(styles.citationContainer)}>
					<h3 className={css(styles.citationTitle)}>View Citations</h3>
					<p className={css(styles.citationContent)}>{template?.citation || 'No citation available.'}</p>
					<button className={css(baseStyleSheet.ctaButton)} onClick={handleCloseCitations}>
						Close
					</button>
				</div>
			</ReactModal>
		</div>
	);
};
